"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Gauge, Layers, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const specifications = [{
  title: "10x Photon Collection",
  subtitle: "Patented HTVS™ optical design eliminates traditional slit trade-offs.",
  icon: Zap
}, {
  title: "Millisecond Sampling",
  subtitle: "High signal-to-noise ratio enables sub-second reaction monitoring.",
  icon: Gauge
}, {
  title: "Process & Lab Ready",
  subtitle: "Direct integration into bioreactors, crystallizers, and flow pipes.",
  icon: Cpu
}, {
  title: "21 CFR Part 11 Compliant",
  subtitle: "Full audit trails, data integrity, and automated IQ/OQ validation protocols.",
  icon: ShieldCheck
}];
const technicalHighlights = ["High-Throughput Virtual Slit (HTVS™) optical bench", "Fiber-coupled immersion and non-contact probes", "Continuous quantitative monitoring for PAT workflows", "Seamless chemometric modeling & OPC UA integration"];
export default function HyperfluxHero() {
  return <section id="hyperfluxproplushero" data-nav="light" className="relative bg-background text-foreground py-16 lg:py-24 overflow-hidden border-b border-border">
      {/* Background Subtle Gradient Grids */}
      <div className="absolute inset-0 bg-radial-[circle_at_top_right] from-primary/5 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 size-96 bg-accent/40 rounded-full blur-3xl pointer-events-none" />

      <div className="page-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Information & Positioning */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-3 py-1 font-medium">
                <Sparkles className="size-3.5 mr-1" />
                Next-Generation Raman PAT
              </Badge>
              <Badge variant="secondary" className="px-3 py-1 text-xs">
                Process Analytical Technology
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-[1.15]">
                HyperFlux PRO Plus Raman Spectrometer
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Uncompromising speed and sensitivity for regulated laboratories and manufacturing teams across Ireland and the UK. Equipped with High-Throughput Virtual Slit (HTVS™) optics, the HyperFlux PRO Plus captures up to 10x more light for instant qualitative and quantitative analysis.
              </p>
            </div>

            {/* Feature Checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              {technicalHighlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-medium">{item}</span>
                </div>)}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="h-12 px-6">
                <Link to="/DiscussYourApplicationPage" className="flex items-center gap-2">
                  Discuss Your Application
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6">
                <Link to="/BookADemoPage" className="flex items-center gap-2">
                  Book a Laboratory Demo
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="h-12 px-4 text-muted-foreground hover:text-foreground">
                <Link to="/ProcessAnalyticalTechnologyPage" className="flex items-center gap-2">
                  <FileText className="size-4" />
                  PAT Applications
                </Link>
              </Button>
            </div>

            {/* Trust / Compliance strip */}
            <div className="flex items-center gap-6 pt-4 border-t border-border/80 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>GMP & 21 CFR Part 11 Compliant</span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-2">
                <Activity className="size-4 text-primary" />
                <span>UK & Ireland On-Site Support</span>
              </div>
            </div>
          </div>

          {/* Right Column: Instrument Showcase & Spec Overview */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {/* Primary Visual Instrument Card */}
            <Card className="border border-border/80 shadow-md overflow-hidden bg-card">
              <div className="relative aspect-4/3 bg-muted/40 overflow-hidden flex items-center justify-center p-6 border-b border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_spectrometer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_spectrometer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_spectrometer.png-wvc-sizes" alt="HyperFlux PRO Plus High-Throughput Raman Spectroscopy Instrument" loading="lazy" className="object-contain w-full h-full drop-shadow-md rounded-lg" />
                <div className="absolute top-4 left-4">
                  <Badge variant="default" className="shadow-xs bg-primary text-primary-foreground font-semibold">
                    HTVS™ Technology
                  </Badge>
                </div>
                <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-xs px-3 py-1.5 rounded-md border border-border/60 text-xs font-mono text-foreground shadow-xs">
                  785 nm / 1064 nm Laser
                </div>
              </div>

              <CardContent className="p-6">
                <div className="flex items-center justify-between pb-4">
                  <div>
                    <h3 className="font-default font-semibold text-foreground text-base">Key Technical Positioning</h3>
                    <p className="text-xs text-muted-foreground">High-performance Raman line</p>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">
                    PRO Plus Series
                  </Badge>
                </div>

                <Separator className="mb-4" />

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border/40">
                    <span className="text-xs text-muted-foreground block mb-0.5">Throughput Gain</span>
                    <span className="text-lg font-bold text-foreground">Up to 10x</span>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border/40">
                    <span className="text-xs text-muted-foreground block mb-0.5">Spectral Range</span>
                    <span className="text-lg font-bold text-foreground">150 - 3300 cm⁻¹</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Spec Highlights Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {specifications.map((spec, i) => {
              const Icon = spec.icon;
              return <div key={i} data-index={i} className="p-4 rounded-lg bg-card border border-border shadow-xs flex flex-col justify-between">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                        <Icon className="size-4" />
                      </div>
                      <h4 className="font-semibold text-xs text-foreground">{spec.title}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {spec.subtitle}
                    </p>
                  </div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}